import { Delete as DeleteIcon, Edit as EditIcon } from "@mui/icons-material";
import { IconButton, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField } from "@mui/material";
import type { FC } from "react";
import { useState } from "react";
import AsyncLoadingButton from "../../components/asyncLoadingButton/AsyncLoadingButton";
import DictionaryAccordion from "./DictionaryAccordion";
import EditTagDialog from "./EditTagDialog";
import { useCustomerNoteTypes } from "./useCustomerNoteTypes";

interface AddNoteTypeProps {
  readonly onAdd: (name: string) => Promise<void>;
}

const AddNoteTypeRow: FC<AddNoteTypeProps> = ({ onAdd }) => {
  const [name, setName] = useState("");
  return (
    <TableBody>
      <TableRow>
        <TableCell>
          <TextField fullWidth label="Nazwa" value={name} onChange={(e) => setName(e.target.value)} />
        </TableCell>
        <TableCell>
          <AsyncLoadingButton
            disabled={!name}
            onClick={async () => {
              await onAdd(name);
              setName("");
            }}
          >
            Dodaj
          </AsyncLoadingButton>
        </TableCell>
      </TableRow>
    </TableBody>
  );
};

interface TagRowProps {
  readonly noteType: { id: string; name: string };
  readonly onNameChange: (name: string) => Promise<void>;
  readonly onDelete: () => void | Promise<void>;
}

const NoteTypeRow: FC<TagRowProps> = ({ noteType, onNameChange, onDelete }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  return (
    <TableRow data-testid="note-type-row">
      <TableCell>{noteType.name}</TableCell>
      <TableCell>
        <Stack direction="row" spacing={2}>
          <IconButton size="small" aria-label="Edytuj typ notatki" onClick={() => setDialogOpen(true)}>
            <EditIcon />
          </IconButton>
          <IconButton onClick={onDelete} aria-label="Usuń typ notatki ze słownika">
            <DeleteIcon />
          </IconButton>
        </Stack>
        <EditTagDialog
          open={dialogOpen}
          tagName={noteType.name}
          onChange={onNameChange}
          onClose={() => setDialogOpen(false)}
        />
      </TableCell>
    </TableRow>
  );
};

const CustomerNoteTypeDictionary = () => {
  const { noteTypes, isLoading, addNoteType, updateNoteTypeName, deleteNoteType } = useCustomerNoteTypes();
  return (
    <DictionaryAccordion title="Typy notatek klienta" isLoading={isLoading}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Nazwa</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <AddNoteTypeRow
          onAdd={async (t) => {
            await addNoteType(t);
          }}
        />
        <TableBody>
          {noteTypes.map((t) => (
            <NoteTypeRow
              key={t.id}
              noteType={t}
              onNameChange={async (name) => {
                await updateNoteTypeName(t.id, name);
              }}
              onDelete={() => deleteNoteType(t.id)}
            />
          ))}
        </TableBody>
      </Table>
    </DictionaryAccordion>
  );
};

export default CustomerNoteTypeDictionary;
